/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { ReactNode } from 'react';
import { css, styled } from '@superset-ui/core';

/** 与其它 explore Popover 一致的分组标题（左侧色条） */
export const SectionTitle = styled.div`
  ${({ theme }) => css`
    font-size: ${theme.typography.sizes.s}px;
    font-weight: ${theme.typography.weights.bold};
    color: ${theme.colors.grayscale.dark2};
    margin: ${theme.gridUnit * 4}px 0 ${theme.gridUnit * 2}px;
    padding: 0 0 0 ${theme.gridUnit}px;
    border-left: 3px solid ${theme.colors.primary.base};

    &:first-child {
      margin-top: 0;
    }
  `}
`;

/** 分组内容：仅保留纵向间距，无卡片背景/边框 */
export const SectionContent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.gridUnit * 2}px;
    margin-bottom: ${theme.gridUnit}px;
  `}
`;

/** 全宽字段区（Select / Input） */
export const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

/** 两列等宽：成对 InputNumber / Select */
export const FieldPair = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${theme.gridUnit * 3}px;
  `}
`;

/** 三列等宽：纯数字参数 */
export const FieldTriple = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.gridUnit * 3}px;
  `}
`;

/** Switch 行：左标签右控件 */
export const ControlRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.gridUnit * 3}px;
    min-height: 32px;

    .control-row-label {
      flex: 1;
      min-width: 0;
    }

    .control-row-control {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  `}
`;

export const ControlRowLabel = ({ children }: { children: ReactNode }) => (
  <span className="control-row-label">{children}</span>
);

export const ControlRowControl = ({ children }: { children: ReactNode }) => (
  <div className="control-row-control">{children}</div>
);

/** 颜色预览：与 TextFormattingPopoverContent 同款 */
export const PreviewStrip = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${theme.gridUnit * 2}px;
  `}
`;

export const PreviewCell = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.gridUnit}px;
  `}
`;

export const PreviewCaption = styled.label`
  ${({ theme }) => css`
    padding: 0 0 ${theme.gridUnit}px;
    margin: 0;
    font-size: ${theme.typography.sizes.s}px;
    color: ${theme.colors.grayscale.dark1};
  `}
`;

export const PreviewSwatch = styled.div<{ $color: string }>`
  ${({ theme, $color }) => css`
    width: 100%;
    height: 30px;
    background-color: ${$color};
    border: 1px solid ${theme.colors.grayscale.light2};
    border-radius: ${theme.gridUnit}px;
    margin-bottom: ${theme.gridUnit * 2}px;
  `}
`;

export const PreviewSample = styled.div<{ $color: string }>`
  ${({ theme, $color }) => css`
    width: 100%;
    height: 30px;
    display: flex;
    align-items: center;
    color: ${$color};
    background-color: ${theme.colors.grayscale.light5};
    border: 1px solid ${theme.colors.grayscale.light2};
    border-radius: ${theme.gridUnit}px;
    margin-bottom: ${theme.gridUnit * 2}px;
    padding: 0 ${theme.gridUnit * 2.75}px;
    font-weight: ${theme.typography.weights.bold};
  `}
`;

export const JustifyEnd = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/** Popover 滚动区：与右侧滚动条留出间距 */
export const PopoverScrollBody = styled.div`
  ${({ theme }) => css`
    width: 450px;
    max-height: 60vh;
    overflow: auto;
    box-sizing: border-box;
    padding: 0 ${theme.gridUnit * 3}px ${theme.gridUnit * 2}px;
    scrollbar-gutter: stable;
  `}
`;
